import { Pagination } from '@api/entities';
import HundredBricksAPI from '../../HundredBricksAPI';
import { APIError } from '../../../api';
import { AmenitiesError, Amenity } from '../amenities';
import ManagementLogs, { ManagementLog, ManagementLogsError } from '../managementLogs';
import OnSaleReportStatus, { OnSaleStatusReportError } from './latestOnSalePropertyStatusReport';
import { IncomePeriod, IncomeHistoryError, IncomePeriodsHistory } from './incomeHistory';
import { OnSaleProperties, OnSaleProperty, OnSalePropertiesError, OnSalePropertiesInvalidQueryParam } from './onSaleProperty';
class OnSalePropertyAPI extends HundredBricksAPI {
    async getIncomeHistory(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/dispersions?sortedBy=-createdAt&status=success`);
            return new IncomePeriodsHistory({
                incomeDispersions: data.items.map(incomePeriod => new IncomePeriod(incomePeriod)),
                totals: data.totals,
                pagination: new Pagination(data.pagination),
                count: data.count
            });
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new IncomeHistoryError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new IncomeHistoryError('Unexpected income history error', { cause: error });
            }
            else {
                throw new IncomeHistoryError(`Unexpected income history error: '${error}'`);
            }
        }
    }
    async getAmenities(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/amenities`);
            return data.items.map(amenity => new Amenity(amenity));
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new AmenitiesError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new AmenitiesError('Unexpected on sale property amenities error', { cause: error });
            }
            else {
                throw new AmenitiesError(`Unexpected on sale property amenities error: '${error}'`);
            }
        }
    }
    async getProperties(params) {
        try {
            const { data } = await this.client.get('v2/properties', { params });
            const properties = data.items.map(property => new OnSaleProperty(property));
            const pagination = new Pagination(data.pagination);
            return new OnSaleProperties({ ...data, pagination, items: properties });
        }
        catch (error) {
            if (error instanceof APIError) {
                switch (error.code) {
                    case 'PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new OnSalePropertiesInvalidQueryParam('page', params?.page, { cause: error });
                    case 'PER_PAGE_CANNOT_BE_LESS_THAN_ONE':
                        throw new OnSalePropertiesInvalidQueryParam('perPage', params?.perPage, { cause: error });
                    case 'INVALID_PAGE':
                        throw new OnSalePropertiesInvalidQueryParam('page', params?.page, { cause: error });
                    case 'INVALID_PER_PAGE':
                        throw new OnSalePropertiesInvalidQueryParam('perPage', params?.perPage, { cause: error });
                    default:
                        throw new OnSalePropertiesError({ cause: error });
                }
            }
            else if (error instanceof Error) {
                throw new OnSalePropertiesError('Unexpected error when obtaining onsale properties', { cause: error });
            }
            else {
                throw new OnSalePropertiesError(`Unexpected error when obtaining onsale properties: '${error}'`);
            }
        }
    }
    async getPropertyManagementLogs(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/managementLogs`);
            const managementLogs = data.items.map(log => new ManagementLog(log));
            return new ManagementLogs(managementLogs);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new ManagementLogsError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new ManagementLogsError('Unexpected error when obtaining onsale property management logs', { cause: error });
            }
            else {
                throw new ManagementLogsError(`Unexpected error when obtaining onsale property management logs: '${error}'`);
            }
        }
    }
    async getLatestOnSalePropertyStatusReport(propertyId) {
        try {
            const { data } = await this.client.get(`v2/properties/${propertyId}/statusReports/latest`);
            return new OnSaleReportStatus(data);
        }
        catch (error) {
            if (error instanceof APIError) {
                throw new OnSaleStatusReportError({ cause: error });
            }
            else if (error instanceof Error) {
                throw new OnSaleStatusReportError('Unexpected error while retrieving on sale property status report', { cause: error });
            }
            else {
                throw new OnSaleStatusReportError(`Unexpected error while retrieving on sale property status report: '${error}'`);
            }
        }
    }
}
export default OnSalePropertyAPI;
